/**
 * THE CLOUD NUDGE — decide whether to tell a player to load before they play.
 *
 * HOWARD, 17 Aug 2026, verbatim, is the specification for this whole file:
 *
 *   "like the project I made at golf-slop.com the user needs to save from
 *    whatever computer or phone theyre using and then upon opening the game on
 *    a new location they must be encouraged strongly to download from the cloud
 *    first. Save and load manually is the key to keeping progress straight
 *    without burning up the I/O at supabase on auto saves."
 *
 * Three requirements live in that sentence:
 *
 *   1. MANUAL SAVE AND LOAD ONLY. There is no autosave in this codebase and
 *      there must never be one. The reason Howard gives is cost — Supabase I/O
 *      — and it is a good one: a season payload is megabytes compressed, and an
 *      autosave on every race would write that repeatedly for a game nobody
 *      asked to have written. See `markLocalProgress` below, which is the exact
 *      place a future reader will be tempted to "just save it while we're here".
 *
 *   2. A NEW LOCATION MUST BE ENCOURAGED TO LOAD FIRST. Before playing, not
 *      after. Playing first creates local work, and then the player has to pick
 *      between two states that both look like theirs.
 *
 *   3. NOTHING IS DESTROYED SILENTLY. The dangerous case is local progress that
 *      is OLDER than the cloud save: loading is the RIGHT call and it still
 *      throws away work the player did. That case gets its own message, says
 *      both timestamps out loud, and is marked destructive so the interface can
 *      make the player confirm.
 *
 * WHY THE DECISION IS A PURE FUNCTION IN ITS OWN FILE.
 *
 * Every branch of this is a sentence a player reads at the moment they are most
 * likely to lose a season, and the difference between the safe branch and the
 * destructive one has to be provable rather than eyeballed in a browser. So
 * `decideCloudNudge` takes plain data, returns plain data, touches no network,
 * no clock and no DOM, and `cloudNudge.test.ts` walks the entire input matrix.
 * `CloudNudge.tsx` renders what this returns and decides nothing.
 *
 * THIS FILE IMPORTS NOTHING. Not even `leagueSlot.ts`, whose `formatSavedAt`
 * would have been convenient — importing it would drag the Supabase client into
 * a module whose whole value is that it is pure and instant to test. The one
 * cost of that choice is a second date formatter, which is why the one below is
 * deliberately unlike the bar's: see `stamp`.
 */

// ---------------------------------------------------------------------------
// What the decision is made from
// ---------------------------------------------------------------------------

/** The label columns of a cloud save. Exactly what `peekSlot` can see. */
export interface CloudSave {
  leagueName: string;
  seasonNumber: number;
  /**
   * WHICH OF THE TWO THINGS THE ONE SLOT IS HOLDING.
   *
   *   'career' — a settled season. Loading it gives you a record book and the
   *              winter's roster to start the NEXT season from.
   *   'season' — a season IN PROGRESS. Loading it puts the player back at the
   *              exact next race of a championship they are halfway through.
   *
   * REQUIRED, not optional with a default. The two are worth very different
   * amounts to the person reading the banner — "your saved league is waiting" is
   * a reminder about a record book and an urgent instruction about twenty races
   * of a live season — and a field that could be left off would be left off, and
   * would silently describe every season in progress as a finished one.
   */
  kind: 'career' | 'season';
  /**
   * Races in the books, for a season in progress. Null for a finished season —
   * NOT 0 and NOT 36. "This save does not count races" is a different fact from
   * "this save is at race 0", and only one of them is true of a career.
   */
  racesRun: number | null;
  /** ISO 8601, stamped by the server clock — not the browser's. */
  savedAt: string;
}

/**
 * D-004 — a season is thirty-six races.
 *
 * WRITTEN OUT rather than imported from `domain/schedule.ts`, because importing
 * it would drag the track-card JSON into a module whose whole value is that it is
 * pure and instant (see the note about imports at the top of this file). The
 * drift that copying invites is closed by a test: `cloudNudge.test.ts` imports the
 * real `SEASON_RACES` and asserts this equals it, so the two cannot part company
 * silently.
 */
export const NUDGE_SEASON_RACES = 36;

/**
 * What we know about the cloud slot. FOUR states, not two: "we did not look"
 * and "we looked and there is nothing" are different facts and a player who is
 * told the wrong one will make the wrong decision.
 */
export type CloudState =
  /** Nobody is signed in, so there is no slot to read. Not a failure. */
  | { state: 'signed-out' }
  /** We tried and could not find out. `reason` is `peekSlot`'s own sentence. */
  | { state: 'unknown'; reason: string }
  /** Signed in, looked, nothing saved. */
  | { state: 'empty' }
  /** Signed in, and this is what is in the slot. */
  | { state: 'save'; save: CloudSave };

/**
 * What this browser holds that a load would replace.
 *
 * `what` is supplied by the caller because only the caller knows what the
 * progress IS — a race run in this tab, a season on the workbench — and a
 * message that names it ("the exhibition race you ran here") is worth far more
 * than one that says "your progress".
 *
 * `lastTouchedAt` may be null, and null is not "old". It means we do not know
 * when the local work happened, and the decision below says exactly that rather
 * than picking a winner. Guessing here would be guessing about which of two
 * seasons survives.
 */
export type LocalState =
  | { state: 'none' }
  | { state: 'progress'; what: string; lastTouchedAt: string | null }
  /**
   * THE WORK IN THIS BROWSER IS THE SAVE. Nothing has been played since.
   *
   * ⚠ THIS STATE WAS MISSING, AND ITS ABSENCE WAS A BUG HOWARD HIT MID-RACE.
   *
   * He saved race 1 successfully and was immediately shown
   * "LOADING WILL DISCARD NEWER-LOOKING WORK IN THIS BROWSER", whose body told
   * him his season "was never saved anywhere" — forty seconds after it was.
   *
   * The cause was that this module could only ever be told two things: that a
   * save exists in the cloud, and that this browser has work. Nothing told it a
   * SAVE HAD JUST HAPPENED. So the cloud's fresh server timestamp came out
   * later than the browser's last-played marker, the age comparison read that
   * as drift, and it warned about a conflict that could not exist.
   *
   * The fix is not a smarter timestamp comparison — it is a FACT. When a save
   * succeeds we KNOW the browser and the slot hold the same season, because we
   * just put it there. That is recorded, and it survives until the next race is
   * run, which is the moment it stops being true.
   */
  | { state: 'saved'; what: string; savedAt: string | null };

export interface NudgeInput {
  cloud: CloudState;
  local: LocalState;
  /**
   * Has this browser been used with an account before?
   *
   * FALSE IS THE "NEW LOCATION" IN HOWARD'S SENTENCE. It is what separates
   * "you are opening the game somewhere new" from "you are back where you were
   * yesterday", and it is the difference between a warning and a reminder.
   */
  seenHereBefore: boolean;
}

// ---------------------------------------------------------------------------
// What comes out
// ---------------------------------------------------------------------------

/**
 * The situation, named. One kind per genuinely different circumstance — if two
 * situations shared a kind they would share a message, and a nudge that says
 * the same thing in a safe case and a destructive one is worse than none.
 */
export type NudgeKind =
  /** Signed out. We cannot see whether a league is saved anywhere. */
  | 'signed-out'
  /** Signed in, but the slot could not be read. */
  | 'cloud-unreadable'
  /** Signed in, looked, nothing is saved. */
  | 'no-cloud-save'
  /** A save exists and this browser holds nothing that a load would replace. */
  | 'load-first'
  /** A save exists, local work is OLDER. Loading is right AND costs work. */
  | 'local-older-than-cloud'
  /** A save exists, local work is NEWER. Loading would undo the newer work. */
  | 'local-newer-than-cloud'
  /** A save exists and the two stamps are identical. Same age, not same state. */
  | 'local-same-age-as-cloud'
  /** A save exists and we cannot tell which is newer. Never guessed. */
  | 'local-age-unknown'
  /** The browser and the slot hold the same season — the player just saved. */
  | 'in-sync';

/**
 * How loudly to say it.
 *
 *   notice — worth knowing, nothing is at stake this second
 *   strong — do this before you play; the whole point of the feature
 *   danger — acting, or failing to act, loses work that cannot come back
 */
export type NudgeLevel = 'notice' | 'strong' | 'danger';

export interface NudgeDecision {
  /** Whether to put anything on screen at all. */
  show: boolean;
  kind: NudgeKind;
  level: NudgeLevel;
  /** Headline. Short, and never a question the player cannot answer. */
  heading: string;
  /** The situation in full sentences, naming leagues, seasons and stamps. */
  body: string;
  /** What to do, imperative, first thing. */
  advice: string;
  /**
   * True when the obvious next action would destroy something. The interface
   * must not offer a one-click load in this state — `CloudNudge.tsx` shows a
   * confirmation that repeats `consequence` first.
   */
  destructive: boolean;
  /**
   * Exactly what pressing Load will do to the work in this browser, or null
   * when there is nothing to lose. This is the sentence a confirmation shows;
   * it is written here rather than in the component so it is under test.
   */
  consequence: string | null;
}

// ---------------------------------------------------------------------------
// Time — compared honestly, or not at all
// ---------------------------------------------------------------------------

/** The three answers, plus the fourth that most code forgets to have. */
export type AgeComparison = 'older' | 'newer' | 'same' | 'unknown';

/**
 * Compare two ISO stamps.
 *
 * Returns `'unknown'` when either side is missing or unparseable, and that is
 * the whole reason this is a named function rather than `a < b`. A missing
 * timestamp compared with `<` silently becomes "older", and "older" is the
 * branch that tells a player it is safe to throw their work away.
 */
export function compareAges(local: string | null, cloud: string | null): AgeComparison {
  const a = local === null ? NaN : Date.parse(local);
  const b = cloud === null ? NaN : Date.parse(cloud);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 'unknown';
  if (a < b) return 'older';
  if (a > b) return 'newer';
  return 'same';
}

/**
 * A timestamp in a sentence that has to be unambiguous.
 *
 * DELIBERATELY NOT `formatSavedAt` FROM `leagueSlot.ts`. That one is friendly
 * ("yesterday at 4:12 PM") because it labels a slot the player already knows
 * about. This one appears in sentences where two saves are being told apart,
 * where "yesterday" and "yesterday" would be indistinguishable, and it is fixed
 * to UTC so that the string is the same in a test as it is on a laptop in
 * Nevada. An unparseable stamp prints as a stated unknown, never as a date.
 */
export function stamp(iso: string | null): string {
  if (iso === null) return 'an unknown time';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'an unrecorded time';
  const d = new Date(t);
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
    `${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`
  );
}

/**
 * "RASCAL — season 4, finished, saved 2026-08-17 01:35 UTC", or
 * "RASCAL — season 4 in progress, 14 of 36 races run, saved …".
 *
 * ONE FORMATTER FOR EVERY BRANCH, so the safe message and the destructive one
 * cannot end up describing the same slot two different ways — which is the
 * failure this whole module exists to prevent, one level down.
 *
 * A season in progress says its race count because that is the number the player
 * is actually weighing. "Season 4" tells them nothing about whether loading is
 * urgent; "14 of 36 races run" tells them exactly how much is in the cloud that
 * is not in front of them.
 */
function describeSave(save: CloudSave): string {
  if (save.kind === 'season') {
    const run = save.racesRun ?? 0;
    return `${save.leagueName} — season ${save.seasonNumber} in progress, ${run} of ${NUDGE_SEASON_RACES} races run, saved ${stamp(save.savedAt)}`;
  }
  return `${save.leagueName} — season ${save.seasonNumber}, finished, saved ${stamp(save.savedAt)}`;
}

/** The sentence about where the player is sitting. Never the whole message. */
function locationLine(seenHereBefore: boolean): string {
  return seenHereBefore
    ? 'This browser has been used with an account before.'
    : 'This browser has never been used with an account, so it is a new phone, computer or profile as far as the game can tell.';
}

// ---------------------------------------------------------------------------
// The decision
// ---------------------------------------------------------------------------

/**
 * Should we nudge, and with what words?
 *
 * The order of the branches is the order the questions actually matter in:
 * can we see the cloud at all → is anything in it → does this browser hold work
 * → which of the two is newer. Every branch returns a complete decision; there
 * is no fall-through and no default that could ever describe a situation it was
 * not written for.
 */
export function decideCloudNudge(input: NudgeInput): NudgeDecision {
  const { cloud, local, seenHereBefore } = input;
  const here = locationLine(seenHereBefore);
  const hasLocal = local.state === 'progress';
  const localLine = hasLocal
    ? `This browser holds ${local.what}, last touched ${stamp(local.lastTouchedAt)}.`
    : 'This browser has no record of unsaved work.';

  /**
   * ---- 0. THE BROWSER AND THE SLOT ARE THE SAME SEASON --------------------
   *
   * ⚠ FIRST, BEFORE EVERY OTHER QUESTION, and it has to be: this is the one
   * state that is KNOWN rather than inferred, and the timestamp comparisons
   * below cannot see it.
   *
   * Howard, 17 Aug 2026, mid-race, having just saved successfully: *"and what
   * does this mean? Im literally in the middle of my first race..."* — under a
   * banner reading LOADING WILL DISCARD NEWER-LOOKING WORK, whose body told him
   * his season "was never saved anywhere". The save had landed forty seconds
   * earlier. The slot's server timestamp was simply later than the browser's
   * last-played marker, and the age comparison read that gap as drift.
   *
   * There is nothing to warn about here and so nothing is said. A banner that
   * fires on a correct, successful save is the fastest way to teach someone to
   * dismiss banners without reading them — and the next one will be real.
   */
  if (local.state === 'saved' && cloud.state === 'save') {
    return {
      show: false,
      kind: 'in-sync',
      level: 'notice',
      heading: 'Saved',
      body:
        `${describeSave(cloud.save)} is in your slot, and it is what is in this browser — ` +
        `saved ${stamp(local.savedAt)}. Nothing has been played since.`,
      advice: 'Nothing to do. Play on; save again when you want the slot to catch up.',
      destructive: false,
      consequence: null,
    };
  }

  // ---- 1. we cannot see the slot ----------------------------------------
  if (cloud.state === 'signed-out') {
    return {
      show: true,
      kind: 'signed-out',
      // A new location is the case Howard named, and it is the one where
      // playing first is most likely to cost something. A browser that has
      // already been used with an account gets the same facts, quieter.
      level: seenHereBefore ? 'notice' : 'strong',
      heading: seenHereBefore ? 'Sign in to reach your saved league' : 'Load your league before you play',
      body:
        'You are signed out, so the game cannot see whether you have a league saved in the cloud. ' +
        `${here} ${localLine} ` +
        'Nothing here is saved automatically — saving and loading are both things you press.',
      advice: 'Sign in, then press Load league — before you start a race, not after.',
      // Nothing is destroyed by signing in, and we do not know there is a save
      // to conflict with. The warning is about the ORDER of operations.
      destructive: false,
      consequence: hasLocal
        ? `If you sign in and load later, the loaded league replaces ${local.what} that is on the workbench now.`
        : null,
    };
  }

  if (cloud.state === 'unknown') {
    return {
      show: true,
      kind: 'cloud-unreadable',
      level: 'strong',
      heading: 'Your saved league could not be read',
      body:
        `${cloud.reason} That is not the same as having nothing saved — assume your league is still there. ` +
        `${here} ${localLine}`,
      // The genuinely dangerous move here is saving over a slot we could not
      // read, so the advice is about NOT doing that, not about loading.
      advice: 'Try again in a moment. Do not save over the slot until it can be read — a save you cannot see is a save you can overwrite blind.',
      destructive: false,
      consequence: null,
    };
  }

  // ---- 2. signed in, nothing in the slot ---------------------------------
  if (cloud.state === 'empty') {
    if (!hasLocal) {
      return {
        show: false,
        kind: 'no-cloud-save',
        level: 'notice',
        heading: 'Nothing saved yet',
        body: `You have no league in the cloud, and this browser has no record of unsaved work. ${here}`,
        advice: 'Play. When a season is worth keeping, press Save league — nothing is written for you.',
        destructive: false,
        consequence: null,
      };
    }
    return {
      show: true,
      kind: 'no-cloud-save',
      level: 'notice',
      heading: 'This work is only in this browser',
      body:
        `You have nothing saved in the cloud yet. ${localLine} ` +
        'It exists nowhere else: close this tab, clear this browser, or pick up your phone instead, and it is gone. ' +
        // The location is worth saying even here, where nothing is at risk from
        // a load: a player who has started a season at a new location is the
        // one most likely to leave it behind by opening the game somewhere else.
        here,
      advice: 'Press Save league to put it in the cloud. There is no autosave — by design.',
      destructive: false,
      consequence: null,
    };
  }

  // ---- 3. there is a save, and nothing here to lose ----------------------
  const save = cloud.save;
  /**
   * THE EXTRA SENTENCE A SEASON IN PROGRESS EARNS.
   *
   * A finished season in the slot is a record book: not loading it costs you a
   * look at last year. A season in progress is a championship in flight, and not
   * loading it means starting a second one — which is the mistake that produces
   * two half-played seasons and one slot to keep them in. Said only where it is
   * true, so it does not become wallpaper on the branches where it is not.
   */
  const inFlight =
    save.kind === 'season'
      ? ` That season is not finished — race ${(save.racesRun ?? 0) + 1} of ${NUDGE_SEASON_RACES} is next. Start a season here instead and you will have two of them and one slot.`
      : '';

  if (!hasLocal) {
    return {
      show: true,
      kind: 'load-first',
      // The new-location case is the one Howard asked to be pushed hard.
      level: seenHereBefore ? 'notice' : 'strong',
      heading:
        save.kind === 'season'
          ? 'Your season is part-run in the cloud'
          : seenHereBefore
            ? 'Your saved league is waiting'
            : 'Load your league before you play',
      body:
        `${describeSave(save)} is in your slot.${inFlight} ${here} ${localLine} ` +
        'Loading now costs you nothing; loading after you have played replaces whatever you have done in the meantime.',
      advice:
        save.kind === 'season'
          ? 'Press Load league now — it picks the season up at the exact next race.'
          : 'Press Load league now, before the first race.',
      destructive: false,
      consequence: null,
    };
  }

  // ---- 4. there is a save AND work here. Which is newer? -----------------
  const age = compareAges(local.lastTouchedAt, save.savedAt);
  // The opening of all four "there is a save AND there is work here" branches:
  // both artefacts named, then where the player is sitting. The location line
  // is part of it because a browser that has never seen an account is a browser
  // whose local work was made BEFORE anyone thought to load — which is exactly
  // the mistake this whole module is trying to head off.
  const both = `${describeSave(save)}.${inFlight} ${localLine} ${here}`;

  if (age === 'older') {
    // THE DANGEROUS CASE, and the reason this module exists. Loading is almost
    // certainly correct — the cloud holds later work — and it still destroys
    // something. Both facts are said, in that order, and the caller must
    // confirm.
    return {
      show: true,
      kind: 'local-older-than-cloud',
      level: 'danger',
      heading: 'Loading will discard newer-looking work in this browser',
      body:
        `${both} The cloud save is the LATER of the two, so loading is probably what you want — ` +
        `but ${local.what} in this browser was never saved anywhere, and loading replaces it. ` +
        'There is no undo and no second slot.',
      advice: `Decide which one is the real league. To keep both, press Save league first — that overwrites ${save.leagueName} in the cloud with what is here — then load nothing.`,
      destructive: true,
      consequence:
        `Load replaces ${local.what} (last touched ${stamp(local.lastTouchedAt)}) with ${describeSave(save)}. ` +
        'The work in this browser is not written anywhere and cannot be recovered.',
    };
  }

  if (age === 'newer') {
    return {
      show: true,
      kind: 'local-newer-than-cloud',
      level: 'danger',
      heading: 'The work in this browser is newer than your saved league',
      body:
        `${both} This browser holds the LATER of the two. Loading would roll your league back to the cloud copy ` +
        'and throw away everything done here since it was saved.',
      advice: 'Press Save league to put this newer work in the cloud. Only load if you meant to abandon it.',
      destructive: true,
      consequence:
        `Load rolls you back to ${describeSave(save)} and discards ${local.what} (last touched ${stamp(
          local.lastTouchedAt,
        )}), which is newer. There is no undo.`,
    };
  }

  if (age === 'same') {
    return {
      show: true,
      kind: 'local-same-age-as-cloud',
      level: 'strong',
      heading: 'Your saved league and this browser are stamped at the same moment',
      body:
        `${both} The two stamps are identical, which usually means this is the browser the save was made from — ` +
        'but an identical timestamp is not proof that the two hold the same season.',
      advice: 'If you have played since saving, save again before loading. Otherwise loading changes nothing you can see.',
      destructive: true,
      consequence: `Load replaces ${local.what} with ${describeSave(save)}. They are stamped alike, but nothing checks that they match.`,
    };
  }

  // age === 'unknown' — one of the two stamps is missing or unreadable.
  return {
    show: true,
    kind: 'local-age-unknown',
    level: 'danger',
    heading: 'Which of these is newer cannot be established',
    body:
      `${both} One of the two has no usable timestamp, so the game cannot tell you which is the later league. ` +
      'It will not guess — a guess here picks which season survives.',
    advice: 'Open the Career tab and look at what is on the workbench before you load. If it is worth keeping, save it first.',
    destructive: true,
    consequence: `Load replaces ${local.what} with ${describeSave(save)}, and nothing here can tell you which one you are keeping.`,
  };
}

// ---------------------------------------------------------------------------
// The two markers this browser keeps
// ---------------------------------------------------------------------------

/**
 * ⚠ THESE ARE MARKERS, NOT SAVES.
 *
 * Between them they hold an account id, a timestamp and a noun phrase. No
 * season, no roster, no ledger — a career lives in the cloud slot, put there by
 * a person pressing Save. Anything that starts writing game state here is
 * building the autosave Howard ruled out, in the one place nobody would look
 * for it.
 */
export const SEEN_ACCOUNTS_KEY = 'rwbr.seenAccounts.v1';
export const LOCAL_PROGRESS_KEY = 'rwbr.localProgress.v1';

/**
 * The three methods of `localStorage` used here and nothing else, declared as
 * an interface so the tests run under vitest's `node` environment with no DOM.
 * The same seam, for the same reason, as `nameStore.ts`'s `PoolStorage`.
 */
export interface NudgeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The browser's store, or null off-browser.
 *
 * Wrapped in a try because merely TOUCHING `localStorage` throws in a Safari
 * private window and under a blocking policy — and a nudge module that can
 * throw during boot would take the whole page down to say something optional.
 */
function defaultStorage(): NudgeStorage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const probe = `${LOCAL_PROGRESS_KEY}.probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}

let storageOverride: NudgeStorage | null | undefined;

/** Test seam. Pass null to simulate a browser with storage switched off. */
export function setNudgeStorage(store: NudgeStorage | null | undefined): void {
  storageOverride = store;
}

function storage(): NudgeStorage | null {
  return storageOverride === undefined ? defaultStorage() : storageOverride;
}

/** Every account id this browser has seen signed in. Never an email. */
function readSeen(): string[] {
  const store = storage();
  if (!store) return [];
  try {
    const raw = store.getItem(SEEN_ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string' && x.length > 0);
  } catch {
    return [];
  }
}

/**
 * Has this browser been used with this account before?
 *
 * A null id means nobody is signed in, and the question becomes "has this
 * browser been used with ANY account" — which is the best available answer to
 * "is this a new location", and is stated that way in the message rather than
 * dressed up as certainty.
 *
 * WHEN STORAGE IS UNAVAILABLE THIS RETURNS FALSE, i.e. "new location". That is
 * the cautious direction: a private window genuinely is a place with no history
 * and no local progress, and warning someone who did not need it costs a
 * dismissed banner, while staying quiet costs a season.
 */
export function accountSeenBefore(accountId: string | null): boolean {
  const seen = readSeen();
  if (seen.length === 0) return false;
  return accountId === null ? true : seen.includes(accountId);
}

/** Remember an account id. Returns false when the browser refused to store it. */
export function rememberAccount(accountId: string): boolean {
  const store = storage();
  if (!store || !accountId) return false;
  const seen = readSeen();
  if (seen.includes(accountId)) return true;
  try {
    // Capped: this is a marker list, and an unbounded array in localStorage is
    // a slow leak nobody ever notices. The oldest ids fall off the front.
    const next = [...seen, accountId].slice(-8);
    store.setItem(SEEN_ACCOUNTS_KEY, JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
}

interface ProgressRecord {
  what: string;
  at: string;
  /**
   * True when `at` is the moment this browser's work was PUSHED TO THE SLOT,
   * rather than the moment a race was run. See `LocalState`'s 'saved' member.
   */
  saved?: boolean;
}

/**
 * Record that the player did something in this browser that a load would
 * replace.
 *
 * ⚠ THIS IS THE PLACE A FUTURE READER WILL BE TEMPTED TO ADD AN AUTOSAVE.
 * Do not. Howard, 17 Aug 2026: *"Save and load manually is the key to keeping
 * progress straight without burning up the I/O at supabase on auto saves."*
 * A season payload is several megabytes compressed; writing it on every race —
 * or on every navigation, which is where this kind of thing always ends up —
 * spends the project's Supabase budget on writes nobody asked for and makes
 * "which copy is real" a question with a new answer every few seconds. What
 * goes in here is a timestamp and a noun phrase, and that is all it is for:
 * telling the player, honestly, that there is unsaved work in this browser.
 *
 * ⚠ AND THE TEMPTATION GOT STRONGER, NOT WEAKER. A season in progress is now
 * saveable, and `leagueSlot.saveSeasonSlot` writes only the races the server does
 * not already hold — about 0.3 MB per race instead of the whole season. Cheap
 * enough that "just save it while we're here, it's only one race" is a sentence
 * someone will think. It is still thirty-six unrequested writes a season, still
 * spent on a game nobody asked to have written, and it still makes the player
 * stop being the one who decides which copy is real. Howard's sentence has not
 * changed. Neither has the answer.
 */
export function markLocalProgress(what: string, atISO: string = new Date().toISOString()): boolean {
  const store = storage();
  if (!store) return false;
  try {
    // `saved` is deliberately absent, not false: running a race is exactly the
    // event that makes a previous save stop describing this browser.
    const record: ProgressRecord = { what, at: atISO };
    store.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/**
 * Record that what is in this browser has been PUT IN THE SLOT.
 *
 * Called on a successful save, and on a successful load — both leave the
 * browser and the slot holding the same season, which is the one situation in
 * which this banner has nothing to warn about.
 *
 * ⚠ NOT AN AUTOSAVE, AND NOT A CLAIM THAT ANYTHING WAS WRITTEN BY ITSELF. This
 * is a note that a save the PLAYER pressed has happened. Howard's rule is
 * untouched: nothing here saves itself.
 *
 * The next `markLocalProgress` — the next race run — overwrites this and the
 * browser is ahead of the slot again, which is when the warning becomes true
 * and comes back.
 */
export function markLocalSaved(what: string, atISO: string = new Date().toISOString()): boolean {
  const store = storage();
  if (!store) return false;
  try {
    const record: ProgressRecord = { what, at: atISO, saved: true };
    store.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/**
 * What this browser holds, as far as the marker knows.
 *
 * A record that exists but cannot be read is reported as progress at an unknown
 * time, NOT as "no progress". The fail-safe direction is the one that warns:
 * the cost of a warning nobody needed is a dismissal, and the cost of silence
 * is a career.
 */
export function readLocalProgress(): LocalState {
  const store = storage();
  if (!store) return { state: 'none' };
  let raw: string | null;
  try {
    raw = store.getItem(LOCAL_PROGRESS_KEY);
  } catch {
    return { state: 'none' };
  }
  if (!raw) return { state: 'none' };

  const unreadable: LocalState = {
    state: 'progress',
    what: 'work whose description could not be read',
    lastTouchedAt: null,
  };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return unreadable;
    const record = parsed as Partial<ProgressRecord>;
    const what = typeof record.what === 'string' && record.what.trim() ? record.what.trim() : null;
    const at = typeof record.at === 'string' && Number.isFinite(Date.parse(record.at)) ? record.at : null;
    if (!what) return unreadable;
    // A record that CLAIMS to be a save but carries no readable timestamp is
    // read as ordinary progress. "Saved" with no when is not a fact, and the
    // fail-safe direction here is always the one that warns.
    if (record.saved === true && at !== null) return { state: 'saved', what, savedAt: at };
    return { state: 'progress', what, lastTouchedAt: at };
  } catch {
    return unreadable;
  }
}

/**
 * Forget the local marker.
 *
 * Called after a load succeeds — the work that was here has been replaced by
 * the cloud copy, so continuing to warn about it would be warning about
 * something that no longer exists. Never called on a timer, and never to make
 * a warning go away without the thing it warned about being resolved.
 */
export function clearLocalProgress(): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.removeItem(LOCAL_PROGRESS_KEY);
    return true;
  } catch {
    return false;
  }
}
